import React, { useState } from 'react';
import { Download, Camera, School, Clock, TrendingUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useDemoLang } from './demoLang';

const PERIODS = ['day', 'week', 'month', 'year'];

const DATA_BY_PERIOD = {
  day:   { sessions: [1, 2, 3, 1, 2, 0, 4], effort: [15, 30, 55, 20, 40, 5, 85] },
  week:  { sessions: [6, 8, 5, 9, 7, 10, 12], effort: [120, 180, 90, 210, 150, 240, 280] },
  month: { sessions: [24, 30, 28, 34], effort: [520, 640, 610, 780] },
  year:  { sessions: [80, 110, 140, 170, 200, 220, 240, 260, 280, 300, 320, 350], effort: [1200, 1400, 1600, 1900, 2100, 2400, 2700, 2900, 3200, 3500, 3800, 4200] },
};

const SUBJECTS = [
  { name_fr: 'Maths', name_en: 'Math', pct: 32, color: '#0F4C4C' },
  { name_fr: 'Sciences', name_en: 'Science', pct: 24, color: '#c47a2a' },
  { name_fr: 'Langues', name_en: 'Languages', pct: 18, color: '#8b1f2f' },
  { name_fr: 'Histoire-Géo', name_en: 'History-Geo', pct: 14, color: '#5b5b8f' },
  { name_fr: 'Informatique', name_en: 'ICT', pct: 12, color: '#2d5f4f' },
];

const RECENT_SESSIONS = [
  { time: '19:42', subject_fr: 'Physique', subject_en: 'Physics', tutor: 'Isaac Newton', dur_fr: '14 min', dur_en: '14 min' },
  { time: '17:10', subject_fr: 'Chimie',   subject_en: 'Chemistry', tutor: 'Marie Curie',   dur_fr: '9 min',  dur_en: '9 min' },
  { time: '15:03', subject_fr: 'Maths',    subject_en: 'Math',      tutor: 'Ada Lovelace',  dur_fr: '22 min', dur_en: '22 min' },
  { time: '12:44', subject_fr: 'Histoire', subject_en: 'History',   tutor: 'Nelson Mandela', dur_fr: '11 min', dur_en: '11 min' },
];

const GRADE_TREND = [12.4, 13.1, 13.5, 14.2, 14.8, 15.6, 16.1];

const BarChart = ({ values, color = '#0F4C4C', labels }) => {
  const max = Math.max(...values, 1);
  return (
    <svg viewBox={`0 0 ${values.length * 40 + 20} 120`} className="w-full h-32" data-testid="chart-bar">
      {values.map((v, i) => {
        const h = (v / max) * 90;
        return (
          <g key={i}>
            <rect x={10 + i * 40} y={110 - h} width={28} height={h} rx={6} fill={color} />
            <text x={24 + i * 40} y={104 - h} textAnchor="middle" fontSize="10" fill="#0F4C4C" fontFamily="Fredoka">{v}</text>
            {labels && <text x={24 + i * 40} y={118} textAnchor="middle" fontSize="9" fill="#0F4C4C" opacity="0.6">{labels[i]}</text>}
          </g>
        );
      })}
    </svg>
  );
};

const PieChart = ({ data }) => {
  let a = -Math.PI / 2;
  const R = 55, cx = 60, cy = 60;
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32" data-testid="chart-pie">
      {data.map((s, i) => {
        const angle = (s.pct / 100) * Math.PI * 2;
        const x1 = cx + R * Math.cos(a);
        const y1 = cy + R * Math.sin(a);
        const x2 = cx + R * Math.cos(a + angle);
        const y2 = cy + R * Math.sin(a + angle);
        const large = angle > Math.PI ? 1 : 0;
        const d = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
        a += angle;
        return <path key={i} d={d} fill={s.color} />;
      })}
      <circle cx={cx} cy={cy} r={22} fill="white" />
    </svg>
  );
};

const LineChart = ({ values, color = '#c47a2a' }) => {
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const step = 300 / (values.length - 1);
  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${100 - ((v - min) / range) * 80}`).join(' ');
  return (
    <svg viewBox="0 0 300 110" className="w-full h-28" data-testid="chart-line">
      <path d={path} stroke={color} strokeWidth="3" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      {values.map((v, i) => (
        <circle key={i} cx={i * step} cy={100 - ((v - min) / range) * 80} r={4} fill={color} />
      ))}
    </svg>
  );
};

export default function Reports() {
  const { lang, t } = useDemoLang();
  const [period, setPeriod] = useState('week');
  const [teacherMode, setTeacherMode] = useState(false);
  const d = DATA_BY_PERIOD[period];

  const doExport = (kind) => toast.success(t(`${kind} — export lancé (démo)`, `${kind} — export started (demo)`));
  const shareToParent = () => toast.success(t('Rapport partagé au parent ✅ (démo)', 'Report shared to parent ✅ (demo)'));

  return (
    <div className="space-y-5" data-testid="reports-page">
      {/* Header + period + teacher toggle */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream space-y-3" data-testid="reports-header">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-teal" />
          <h3 className="font-heading text-lg font-bold text-navy">{t('Rapport de progression', 'Progress Report')}</h3>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="period-toggle">
          {PERIODS.map((p) => {
            const active = period === p;
            const label = { day: t('Jour', 'Day'), week: t('Semaine', 'Week'), month: t('Mois', 'Month'), year: t('Année', 'Year') }[p];
            return (
              <button key={p} onClick={() => setPeriod(p)}
                className={`h-10 px-4 rounded-full font-heading font-bold text-sm transition-colors ${active ? 'bg-navy text-white' : 'bg-cream text-navy hover:bg-teal/10'}`}
                data-testid={`period-${p}`}>{label}</button>
            );
          })}
          <label className="ml-auto flex items-center gap-2 cursor-pointer" data-testid="teacher-mode-label">
            <input type="checkbox" checked={teacherMode} onChange={(e) => setTeacherMode(e.target.checked)} className="w-4 h-4 accent-teal" data-testid="teacher-mode" />
            <span className="text-navy/80 text-sm font-semibold inline-flex items-center gap-1"><School className="w-4 h-4 text-teal" /> {t('Vue enseignant', 'Teacher view')}</span>
          </label>
        </div>
      </section>

      {/* Sessions bar chart */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="sessions-chart">
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t('Sessions par période', 'Sessions per period')}
        </div>
        <BarChart values={d.sessions} color="#0F4C4C" />
      </section>

      {/* Subjects pie */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="subjects-chart">
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t('Matières étudiées', 'Subjects studied')}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <PieChart data={SUBJECTS} />
          <ul className="space-y-1.5 min-w-0 flex-1">
            {SUBJECTS.map((s, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="text-navy font-semibold">{lang === 'fr' ? s.name_fr : s.name_en}</span>
                <span className="ml-auto text-navy/60 font-bold">{s.pct}%</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Effort line */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="effort-chart">
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t("Score d'effort — tendance", 'Effort score — trend')}
        </div>
        <LineChart values={d.effort} color="#c47a2a" />
      </section>

      {/* Grade trend */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="grade-trend">
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t('Évolution des notes (/ 20)', 'Grade trend (/ 20)')}
        </div>
        <LineChart values={GRADE_TREND} color="#0F4C4C" />
      </section>

      {/* Recent tutor sessions */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="recent-sessions">
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t('Sessions tuteur récentes', 'Recent tutor sessions')}
        </div>
        <div className="space-y-2">
          {RECENT_SESSIONS.map((r, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-2xl bg-cream" data-testid={`session-${i}`}>
              <div className="w-10 h-10 rounded-full bg-navy text-gold flex items-center justify-center text-xs font-heading font-bold">{r.time}</div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-navy text-sm truncate">{lang === 'fr' ? r.subject_fr : r.subject_en} · {r.tutor}</div>
                <div className="text-navy/60 text-xs inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {lang === 'fr' ? r.dur_fr : r.dur_en}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Export + share */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream space-y-2" data-testid="report-actions">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => doExport('PDF')} className="h-11 px-5 rounded-full bg-navy text-white font-heading font-bold text-sm inline-flex items-center gap-2" data-testid="export-pdf"><Download className="w-4 h-4" /> PDF</button>
          <button onClick={() => doExport('CSV')} className="h-11 px-5 rounded-full bg-teal text-white font-heading font-bold text-sm inline-flex items-center gap-2" data-testid="export-csv"><Download className="w-4 h-4" /> CSV</button>
          {teacherMode && (
            <button onClick={shareToParent} className="h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold text-sm inline-flex items-center gap-2 shadow-gold" data-testid="share-report">
              <Camera className="w-4 h-4" /> {t('Photographier & partager au parent', 'Share to parent')}
            </button>
          )}
        </div>
      </section>

      {/* School adoption CTA */}
      <section className="rounded-3xl p-5 bg-gradient-to-br from-navy to-teal text-white shadow-squishyLg" data-testid="school-cta">
        <div className="flex items-start gap-3">
          <School className="w-6 h-6 text-gold shrink-0" />
          <div>
            <div className="font-heading text-lg font-bold">
              {t("Adopter OuiStuPaStu dans toute l'école", 'Adopt OuiStuPaStu school-wide')}
            </div>
            <p className="text-white/85 text-sm mt-1">
              {t("Tableaux de bord enseignants, gestion de classes, licences volume — parlons-en.",
                 'Teacher dashboards, class management, volume licences — let\'s talk.')}
            </p>
            <button className="mt-3 h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold text-sm inline-flex items-center gap-2 shadow-gold"
              onClick={() => toast.success(t('Message envoyé à léquipe (démo)', 'Message sent to the team (demo)'))}
              data-testid="school-contact">
              <Sparkles className="w-4 h-4" /> {t('Nous contacter', 'Contact us')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
